// Photo-real step-by-step install tutorials driven by Nano Banana images.
// Phase 40.19 — first module: Install a Kitchen Basin.  8 photo-real
// frames the user can step through with real captions, no cartoon.

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, RotateCcw, Home, Loader2, CheckCircle2, Play } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InstallTutorialPage() {
  const { topic = 'basin_kitchen' } = useParams();
  const nav = useNavigate();
  const [manifest, setManifest] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/tutorials/${topic}/manifest`);
      setManifest(data);
    } catch (e) {
      toast.error('Could not load tutorial manifest');
    }
  };
  useEffect(() => { load(); }, [topic]);

  const generate = async () => {
    setBusy(true);
    try {
      toast.info('Rendering photo-real steps — this takes ~1 minute the first time');
      await axios.post(`${API}/api/tutorials/${topic}/generate`, {}, { timeout: 180000 });
      await load();
      toast.success('All steps ready!');
    } catch (e) {
      toast.error('Generation failed — check backend logs');
    } finally {
      setBusy(false);
    }
  };

  const allReady = manifest?.steps?.every((s) => s.ready);
  const current = manifest?.steps?.[step];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <button onClick={() => nav('/labs/engineering-designer')} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="back-to-lab">
              <Home className="w-3.5 h-3.5" /> Young Engineer Lab
            </button>
            <h1 className="text-2xl sm:text-3xl font-black mt-1" data-testid="tutorial-title">
              Install a Kitchen Basin - Photo-real Step-by-Step
            </h1>
            <p className="text-sm text-slate-400 mt-1">Real parts. Real tools. Real order. No cartoons.</p>
          </div>
          <div className="flex items-center gap-2">
            {!allReady && (
              <Button size="sm" onClick={generate} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="generate-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                {busy ? 'Rendering…' : 'Render All Steps'}
              </Button>
            )}
            {allReady && (
              <div className="text-xs text-emerald-300 flex items-center gap-1 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/30">
                <CheckCircle2 className="w-3.5 h-3.5" /> All 8 steps rendered
              </div>
            )}
          </div>
        </div>

        {/* Step progress dots */}
        {manifest && (
          <div className="flex items-center gap-1.5 mb-4" data-testid="step-dots">
            {manifest.steps.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setStep(i)}
                className={`h-1.5 rounded-full transition-all ${i === step ? 'bg-amber-400 w-10' : s.ready ? 'bg-emerald-500 w-6' : 'bg-slate-700 w-6'}`}
                title={s.caption}
                data-testid={`step-dot-${i}`}
              />
            ))}
          </div>
        )}

        {/* Main frame */}
        <div className="relative rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl bg-slate-900 aspect-video" data-testid="tutorial-frame">
          <AnimatePresence mode="wait">
            {current ? (
              <motion.div key={current.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="absolute inset-0">
                {current.ready ? (
                  <img src={`${API}${current.img}`} alt={current.caption} className="w-full h-full object-cover" data-testid="tutorial-img" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 text-sm gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                    <div>This step has not been rendered yet.</div>
                    <div className="text-xs">Click "Render All Steps" up top.</div>
                  </div>
                )}
                {/* Caption bar */}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                  <div className="text-[11px] font-black text-amber-300 uppercase tracking-widest" data-testid="tutorial-step-num">
                    Step {step + 1} of {manifest.steps.length}
                  </div>
                  <div className="text-lg sm:text-xl font-bold text-white mt-1 max-w-3xl" data-testid="tutorial-caption">
                    {current.caption}
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-500">Loading…</div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-4">
          <Button
            size="sm"
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            data-testid="prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <div className="text-xs text-slate-400">
            {manifest ? `${step + 1} / ${manifest.steps.length}` : ''}
          </div>
          <Button
            size="sm"
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
            disabled={!manifest || step >= (manifest.steps.length - 1)}
            onClick={() => setStep((s) => Math.min((manifest?.steps?.length || 1) - 1, s + 1))}
            data-testid="next-btn"
          >
            Next Step <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Parts strip */}
        {manifest && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="thumbs">
            {manifest.steps.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setStep(i)}
                className={`relative rounded-lg overflow-hidden border-2 transition-all aspect-square ${i === step ? 'border-amber-400 shadow-lg' : 'border-white/10 hover:border-white/30'}`}
                data-testid={`thumb-${i}`}
              >
                {s.ready ? (
                  <img src={`${API}${s.img}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-slate-800 flex items-center justify-center text-slate-600 text-xs">…</div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-1 py-1">
                  <div className="text-[9px] text-amber-300 font-bold">STEP {i + 1}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="text-center mt-8 text-xs text-slate-500">
          Images are rendered by Gemini Nano Banana. Regenerate anytime by clicking "Render All Steps".
        </div>
      </div>
    </div>
  );
}
